import React, { useState, useEffect, useCallback } from "react";
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Eye,
    Filter,
    CheckCircle2,
    Clock,
    Loader2,
    Music2,
    Globe,
    Lock,
    Headphones
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDiscography } from "../../services/admin/DiscographyServices";
import ViewDiscographyModal from "../../components/admin/ViewDiscographyModal";
import StreamingPlatformModal from "../../components/admin/StreamingPlatformModal";

export default function DiscographyPage() {
    const navigate = useNavigate();
    const { fetchAllMusic, deleteMusic, toggleMusicStatus, loading } = useDiscography();
    const [musicList, setMusicList] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewModal, setViewModal] = useState({ isOpen: false, itemId: null });
    const [streamingModalOpen, setStreamingModalOpen] = useState(false);

    const loadMusic = useCallback(async () => {
        const data = await fetchAllMusic();
        setMusicList(data || []);
    }, [fetchAllMusic]);



    useEffect(() => {
        loadMusic();
    }, [loadMusic]);

    const handleToggleStatus = async (id) => {
        const res = await toggleMusicStatus(id);
        if (res.status === "success") {
            setMusicList(prev => prev.map(item =>
                item.id === id ? { ...item, status: res.new_status } : item
            ));
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this release? This action cannot be undone.")) {
            const res = await deleteMusic(id);
            if (res.status === "success") {
                setMusicList(prev => prev.filter(item => item.id !== id));
            }
        }
    };

    const filteredMusic = (Array.isArray(musicList) ? musicList : []).filter(item =>
        (item.title || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
        (item.year || '').includes(searchTerm) ||
        (item.category || item.type || '').toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    return (
        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            Discography Management
                        </h1>
                        <p className="text-black dark:text-white/50 text-sm mt-1">
                            Manage your albums, singles, and live recordings.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 w-full md:w-auto">
                        <button
                            onClick={() => setStreamingModalOpen(true)}
                            className="flex items-center justify-center gap-2 bg-white dark:bg-zinc-800 text-black dark:text-white border border-black/10 dark:border-white/10 px-6 py-3 rounded-xl text-sm font-bold tracking-widest hover:bg-gray-50 dark:hover:bg-zinc-700 transition-all w-full md:w-auto shadow-sm"
                        >
                            <Headphones size={18} /> PLATFORM LINKS
                        </button>
                        <button
                            onClick={() => navigate("/admin/discography/add")}
                            className="flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all w-full md:w-auto shadow-lg shadow-black/10"
                        >
                            <Plus size={18} /> ADD NEW MUSIC
                        </button>
                    </div>
                </div>

                {/* Filters & Search */}
                <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-black/60 dark:text-white/40" size={18} />
                        <input
                            type="text"
                            placeholder="Search music..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm text-black dark:text-white placeholder-black/70 dark:placeholder-white/40"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2 w-full md:w-auto">
                        <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] text-sm font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors">
                            <Filter size={16} /> Filters
                        </button>
                    </div>
                </div>

                {/* Music List Table */}
                <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden min-h-[400px]">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-black/5 dark:border-white/5 bg-[#FAFAFA] dark:bg-[#222]">
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Content</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Category</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Year</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Date Added</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5 dark:divide-white/5 relative">
                                {loading && musicList.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <Loader2 className="animate-spin text-jazz-brown" size={32} />
                                                <p className="text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Loading records...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredMusic.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-20 text-center text-black dark:text-white/50 font-medium italic">
                                            No music records found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredMusic.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 rounded-lg bg-[#F5F5F5] dark:bg-zinc-800 flex items-center justify-center overflow-hidden border border-black/5">
                                                        {item.cover_path ? (
                                                            <img
                                                                src={item.cover_path}
                                                                alt={item.title}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <Music2 className="text-black/30" size={24} />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-sm text-black dark:text-white group-hover:text-jazz-brown transition-colors">{item.title}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="px-2.5 py-1 bg-jazz-brown/10 text-jazz-brown dark:bg-zinc-800 dark:text-white/80 rounded text-xs font-bold uppercase tracking-wider">
                                                    {item.category || item.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-black dark:text-white/60">
                                                {item.year}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${item.status === 'active'
                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                                    : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                                                    }`}>
                                                    {item.status === 'active' ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                                                    {item.status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-black dark:text-white/60">
                                                {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end items-center gap-2">
                                                    <button
                                                        onClick={() => setViewModal({ isOpen: true, itemId: item.id })}
                                                        className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                                                        title="View Details"
                                                    >
                                                        <Eye size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleStatus(item.id)}
                                                        className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                                                        title={item.status === 'active' ? "Make Private" : "Make Public"}
                                                    >
                                                        {item.status === 'active' ? <Globe size={18} className="text-green-500" /> : <Lock size={18} />}
                                                    </button>
                                                    <button
                                                        onClick={() => navigate(`/admin/discography/edit/${item.id}`)}
                                                        className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.id)}
                                                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg text-red-400 hover:text-red-600 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Placeholder */}
                    <div className="p-6 border-t border-black/5 dark:border-white/5 flex items-center justify-between font-inter">
                        <p className="text-xs text-black dark:text-white/50 font-bold tracking-widest uppercase">
                            {filteredMusic.length} items found
                        </p>
                        <div className="flex gap-2">
                            <button className="px-4 py-2 text-xs font-bold tracking-widest bg-gray-100 dark:bg-zinc-700 dark:text-white rounded-lg disabled:opacity-50">PREV</button>
                            <button className="px-4 py-2 text-xs font-bold tracking-widest bg-black text-white rounded-lg">NEXT</button>
                        </div>
                    </div>
                </div>

                {/* View Record Modal */}
                <ViewDiscographyModal
                    isOpen={viewModal.isOpen}
                    itemId={viewModal.itemId}
                    onClose={() => setViewModal({ isOpen: false, itemId: null })}
                />

                <StreamingPlatformModal 
                    isOpen={streamingModalOpen} 
                    onClose={() => setStreamingModalOpen(false)} 
                />
            </div>
    );
}
